import { useState, useEffect } from "react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  Paintbrush, Send, Loader2, LogIn, LogOut, Phone, Key, CheckCircle, 
  Image, FileText, Building2, CreditCard,
  User, ArrowRight, Download, RefreshCw, Hash
} from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Bank list from the bot
const BANKS = [
  { id: 'sberbank', name: 'Сбербанк', botCommand: 'Сбербанк' },
  { id: 'tbank', name: 'Т-Банк', botCommand: 'Т-Банк' },
  { id: 'alfabank', name: 'Альфа-Банк', botCommand: 'Альфа-Банк' },
  { id: 'ozonbank', name: 'Ozon Банк', botCommand: 'Ozon Банк' },
  { id: 'gazprombank', name: 'Газпромбанк', botCommand: 'Газпромбанк' },
  { id: 'vtb', name: 'ВТБ Банк', botCommand: 'ВТБ Банк' },
  { id: 'uralsib', name: 'Уралсиб', botCommand: 'Уралсиб' },
  { id: 'akbars', name: 'Ак Барс', botCommand: 'Ак Барс' },
  { id: 'mbank', name: 'MBank', botCommand: 'MBank' },
  { id: 'kaspi', name: 'Каспи', botCommand: 'Каспи' },
];

export default function Drawing() {
  // Auth state
  const [authStatus, setAuthStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authStep, setAuthStep] = useState('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [phoneCodeHash, setPhoneCodeHash] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  
  // Profile & Transaction state
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [selectedBank, setSelectedBank] = useState(null);
  
  // Form state - matching bot fields exactly
  const [formStep, setFormStep] = useState('select');
  const [formData, setFormData] = useState({
    amount: '',              // 1️⃣ Сумма перевода
    receiverPhone: '',       // 2️⃣ Телефон получателя
    receiverBank: '',        // 3️⃣ Банк получателя
    receiverName: '',        // 4️⃣ Имя получателя
    senderName: '',          // 5️⃣ Имя отправителя
    dateTime: '',            // 6️⃣ Дата и время перевода
    sbpNumber: ''            // ▶️ Номер операции СБП (необ.)
  });
  
  // Result state
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [botConversation, setBotConversation] = useState([]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { 
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json"
    };
  };

  useEffect(() => {
    checkAuthStatus();
    fetchProfiles();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/telegram/status`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setAuthStatus(data);
    } catch (error) {
      console.error("Error checking auth status:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfiles = async () => {
    try {
      const response = await fetch(`${API_URL}/api/ozon/profiles`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setProfiles(data);
    } catch (error) {
      console.error("Error fetching profiles:", error);
    }
  };

  // Auth handlers
  const handleSendCode = async () => {
    if (!phone) {
      toast.error("Введите номер телефона");
      return;
    }
    
    setAuthLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/telegram/send-code`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ phone })
      });
      const data = await response.json();
      
      if (data.success) {
        setPhoneCodeHash(data.phone_code_hash);
        setAuthStep('code');
        toast.success("Код отправлен в Telegram");
      } else {
        toast.error(data.detail || "Ошибка отправки кода");
      }
    } catch (error) {
      toast.error("Ошибка подключения");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!code) {
      toast.error("Введите код");
      return;
    }
    
    setAuthLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/telegram/verify-code`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ phone, code, phone_code_hash: phoneCodeHash })
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success("Авторизация успешна!");
        setAuthStatus({ authenticated: true, ...data.user });
        setAuthStep('phone');
        setPhone('');
        setCode('');
      } else if (data.needs_password) {
        setAuthStep('password');
        toast.info("Требуется пароль 2FA");
      } else {
        toast.error(data.detail || "Неверный код");
      }
    } catch (error) {
      toast.error("Ошибка верификации");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleVerifyPassword = async () => {
    if (!password) {
      toast.error("Введите пароль");
      return;
    }
    
    setAuthLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/telegram/verify-password`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ phone, password })
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success("Авторизация успешна!");
        setAuthStatus({ authenticated: true, ...data.user });
        setAuthStep('phone');
        setPhone('');
        setPassword('');
      } else {
        toast.error(data.detail || "Неверный пароль");
      }
    } catch (error) {
      toast.error("Ошибка авторизации");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/telegram/logout`, {
        method: "POST",
        headers: getAuthHeaders()
      });
      setAuthStatus({ authenticated: false });
      setResult(null);
      toast.success("Выход выполнен");
    } catch (error) {
      toast.error("Ошибка выхода");
    }
  };

  // Profile selection
  const handleProfileSelect = (profileId) => {
    const profile = profiles.find(p => p.id === profileId);
    setSelectedProfile(profile);
    setSelectedTransaction(null);
    setFormStep('select');
  };

  // Transaction selection - auto-fill form data from OZON profile
  const handleTransactionSelect = (txId) => {
    const tx = selectedProfile?.transactions?.find(t => t.id === txId);
    setSelectedTransaction(tx);
    
    if (tx) {
      // Auto-fill from OZON profile data
      setFormData({
        amount: tx.amount?.toString() || '',                    // 1️⃣ Сумма перевода
        receiverPhone: selectedProfile?.phone || '',            // 2️⃣ Телефон получателя
        receiverBank: '',                                        // 3️⃣ Банк получателя - MANUAL
        receiverName: selectedProfile?.name || '',              // 4️⃣ Имя получателя
        senderName: tx.method || '',                            // 5️⃣ Имя отправителя
        dateTime: `${tx.date || ''} ${tx.time || ''}`.trim(),  // 6️⃣ Дата и время
        sbpNumber: ''                                            // ▶️ Номер СБП - MANUAL (optional)
      });
    }
  };

  // Bank selection (the bank we're generating receipt FOR)
  const handleBankSelect = (bankId) => {
    const bank = BANKS.find(b => b.id === bankId);
    setSelectedBank(bank);
    setFormStep('fill');
  };

  // Generate check - LOCAL or via Telegram bot
  const handleGenerate = async (useLocal = true) => {
    if (!selectedBank) {
      toast.error("Выберите банк");
      return;
    }

    setProcessing(true);
    setFormStep('generate');
    setBotConversation([]);
    setResult(null);

    if (useLocal) {
      // LOCAL PDF GENERATION (fast, no Telegram)
      setBotConversation([
        { type: 'info', text: '📄 Локальная генерация PDF...' },
      ]);

      try {
        const response = await fetch(`${API_URL}/api/generate-local-check`, {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            bank: selectedBank.botCommand,
            amount: formData.amount,
            receiverPhone: formData.receiverPhone,
            receiverBank: formData.receiverBank,
            receiverName: formData.receiverName,
            senderName: formData.senderName,
            dateTime: formData.dateTime,
            sbpNumber: formData.sbpNumber
          })
        });
        
        const data = await response.json();

        if (data.success && data.pdf) {
          setResult({ pdf: data.pdf, filename: data.filename });
          setFormStep('result');
          toast.success("PDF сгенерирован!");
        } else {
          toast.error(data.detail || "Ошибка генерации");
          setFormStep('fill');
        }

      } catch (error) {
        console.error("Generation error:", error);
        toast.error("Ошибка генерации");
        setFormStep('fill');
      } finally {
        setProcessing(false);
      }
    } else {
      // TELEGRAM BOT GENERATION
      const dataMessage = `${formData.amount}
${formData.receiverPhone}
${formData.receiverBank}
${formData.receiverName}
${formData.senderName}
${formData.dateTime}${formData.sbpNumber ? '\n' + formData.sbpNumber : ''}`;

      setBotConversation([
        { type: 'info', text: `1️⃣ Выбор банка: ${selectedBank.name}` },
        { type: 'info', text: `2️⃣ Перевод через СБП` },
        { type: 'info', text: `3️⃣ Данные:\n${dataMessage}` },
      ]);

      try {
        const response = await fetch(`${API_URL}/api/telegram/generate-check`, {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            bank: selectedBank.botCommand,
            amount: formData.amount,
            receiverPhone: formData.receiverPhone,
            receiverBank: formData.receiverBank,
            receiverName: formData.receiverName,
            senderName: formData.senderName,
            dateTime: formData.dateTime,
            sbpNumber: formData.sbpNumber
          })
        });
        
        const data = await response.json();

        if (data.success && data.image) {
          setResult(data);
          setFormStep('result');
          toast.success("Чек сгенерирован!");
        } else if (data.text) {
          setBotConversation(prev => [...prev, { type: 'received', text: data.text }]);
          setFormStep('result');
          toast.info("Проверьте результат в боте");
        } else {
          toast.error(data.detail || "Ошибка генерации");
          setFormStep('fill');
        }

      } catch (error) {
        console.error("Generation error:", error);
        toast.error("Ошибка генерации");
        setFormStep('fill');
      } finally {
        setProcessing(false);
      }
    }
  };

  const resetForm = () => {
    setSelectedProfile(null);
    setSelectedTransaction(null);
    setSelectedBank(null);
    setFormStep('select');
    setFormData({
      amount: '',
      receiverPhone: '',
      receiverBank: '',
      receiverName: '',
      senderName: '',
      dateTime: '',
      sbpNumber: ''
    });
    setResult(null);
    setBotConversation([]);
  };

  // Check which fields are filled from profile
  const getFieldStatus = (fieldName) => {
    const value = formData[fieldName];
    if (!value) return 'empty';
    
    // These fields are auto-filled from OZON
    const autoFields = ['amount', 'receiverPhone', 'receiverName', 'senderName', 'dateTime'];
    if (autoFields.includes(fieldName) && value) return 'auto';
    
    return 'manual';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="drawing-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
              <Paintbrush className="w-5 h-5 text-white" />
            </div>
            Рисовалка
          </h1>
          <p className="text-slate-500 mt-1">Генерация банковских чеков из OZON профилей</p>
        </div>
        
        {authStatus?.authenticated && (
          <Button
            variant="outline"
            onClick={handleLogout}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Выйти
          </Button>
        )}
      </div>

      {/* Auth Section */}
      {!authStatus?.authenticated ? (
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <LogIn className="w-5 h-5 text-blue-500" />
            Авторизация Telegram
          </h2>
          <p className="text-slate-500 mb-6">
            Для генерации чеков необходимо войти в Telegram с ботом @happychilds_bot
          </p>

          {authStep === 'phone' && (
            <div className="space-y-4 max-w-md">
              <div className="space-y-2">
                <Label>Номер телефона</Label>
                <div className="flex gap-2">
                  <Input placeholder="+7 999 123 45 67" value={phone} onChange={(e) => setPhone(e.target.value)} />
                  <Button onClick={handleSendCode} disabled={authLoading} className="bg-blue-500 hover:bg-blue-600">
                    {authLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Phone className="w-4 h-4 mr-2" />Получить код</>}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {authStep === 'code' && (
            <div className="space-y-4 max-w-md">
              <div className="p-3 bg-blue-50 rounded-lg text-blue-700 text-sm">Код отправлен на {phone}</div>
              <div className="flex gap-2">
                <Input placeholder="12345" value={code} onChange={(e) => setCode(e.target.value)} />
                <Button onClick={handleVerifyCode} disabled={authLoading} className="bg-green-500 hover:bg-green-600">
                  {authLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle className="w-4 h-4 mr-2" />Подтвердить</>}
                </Button>
              </div>
              <Button variant="ghost" onClick={() => setAuthStep('phone')}>← Изменить номер</Button>
            </div>
          )}

          {authStep === 'password' && (
            <div className="space-y-4 max-w-md">
              <div className="p-3 bg-yellow-50 rounded-lg text-yellow-700 text-sm">Требуется пароль 2FA</div>
              <div className="flex gap-2">
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
                <Button onClick={handleVerifyPassword} disabled={authLoading} className="bg-green-500 hover:bg-green-600">
                  {authLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Key className="w-4 h-4 mr-2" />Войти</>}
                </Button>
              </div>
            </div>
          )}
        </Card>
      ) : (
        <>
          {/* Connected */}
          <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span className="text-green-700 font-medium">
              Telegram: {authStatus.telegram_first_name || authStatus.telegram_username}
            </span>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center gap-2 text-sm flex-wrap">
            <span className={`px-3 py-1 rounded-full ${formStep === 'select' ? 'bg-blue-500 text-white' : 'bg-slate-200'}`}>1. Выбор</span>
            <ArrowRight className="w-4 h-4 text-slate-400" />
            <span className={`px-3 py-1 rounded-full ${formStep === 'fill' ? 'bg-blue-500 text-white' : 'bg-slate-200'}`}>2. Данные</span>
            <ArrowRight className="w-4 h-4 text-slate-400" />
            <span className={`px-3 py-1 rounded-full ${formStep === 'generate' ? 'bg-blue-500 text-white' : 'bg-slate-200'}`}>3. Генерация</span>
            <ArrowRight className="w-4 h-4 text-slate-400" />
            <span className={`px-3 py-1 rounded-full ${formStep === 'result' ? 'bg-green-500 text-white' : 'bg-slate-200'}`}>4. Результат</span>
          </div>

          {/* Step 1: Select Profile & Transaction */}
          {formStep === 'select' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Profile Selection */}
              <Card className="p-6">
                <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-blue-500" />
                  Выберите OZON профиль
                </h3>
                <Select onValueChange={handleProfileSelect} value={selectedProfile?.id}>
                  <SelectTrigger>
                    <SelectValue placeholder="Выберите профиль..." />
                  </SelectTrigger>
                  <SelectContent>
                    {profiles.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name || p.dolphin_profile_name} (ID: {p.ozon_id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {selectedProfile && (
                  <div className="mt-4 p-3 bg-slate-50 rounded-lg text-sm space-y-1">
                    <p><strong>Имя:</strong> {selectedProfile.name}</p>
                    <p><strong>Телефон:</strong> {selectedProfile.phone}</p>
                    <p><strong>Транзакций:</strong> {selectedProfile.transactions?.length || 0}</p>
                  </div>
                )}
              </Card>

              {/* Transaction Selection */}
              <Card className="p-6">
                <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-green-500" />
                  Выберите транзакцию
                </h3>
                
                {selectedProfile?.transactions?.length > 0 ? (
                  <div className="space-y-2 max-h-72 overflow-y-auto">
                    {selectedProfile.transactions.map(tx => (
                      <div
                        key={tx.id}
                        onClick={() => handleTransactionSelect(tx.id)}
                        className={`p-3 rounded-lg cursor-pointer transition-all ${
                          selectedTransaction?.id === tx.id 
                            ? 'bg-green-100 border-2 border-green-500' 
                            : 'bg-slate-50 hover:bg-slate-100 border-2 border-transparent'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className={`font-medium ${tx.type === 'deposit' ? 'text-green-600' : 'text-red-600'}`}>
                            {tx.type === 'deposit' ? '+' : '-'}{tx.amount} ₽
                          </span>
                          <span className="text-slate-500 text-sm">{tx.date} {tx.time}</span>
                        </div>
                        <p className="text-slate-600 text-sm mt-1">{tx.method}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500">Выберите профиль</p>
                )}
              </Card>

              {/* Bank Selection */}
              {selectedTransaction && (
                <Card className="p-6 lg:col-span-2">
                  <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-amber-500" />
                    Выберите банк отправителя (чей чек генерируем)
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    {BANKS.map(bank => (
                      <Button
                        key={bank.id}
                        variant="outline"
                        onClick={() => handleBankSelect(bank.id)}
                        className="h-auto py-3 hover:bg-slate-50"
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        {bank.name}
                      </Button>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          )}

          {/* Step 2: Fill Data - Matching Bot Fields */}
          {formStep === 'fill' && (
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-500" />
                  Данные для чека {selectedBank?.name}
                </h3>
                <Button variant="ghost" onClick={() => setFormStep('select')}>← Назад</Button>
              </div>

              {/* Legend */}
              <div className="mb-6 p-3 bg-slate-50 rounded-lg flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded-full bg-green-500"></span>
                  Из OZON профиля
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                  Заполнить вручную
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded-full bg-slate-300"></span>
                  Необязательно
                </span>
              </div>

              <div className="space-y-4">
                {/* 1️⃣ Сумма перевода */}
                <div className="flex items-center gap-4">
                  <span className={`w-3 h-3 rounded-full flex-shrink-0 ${getFieldStatus('amount') === 'auto' ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
                  <div className="flex-1">
                    <Label className="text-sm font-medium">1️⃣ Сумма перевода</Label>
                    <Input 
                      value={formData.amount}
                      onChange={(e) => setFormData(p => ({...p, amount: e.target.value}))}
                      placeholder="2001"
                      className={getFieldStatus('amount') === 'auto' ? 'bg-green-50 border-green-200' : ''}
                    />
                  </div>
                </div>

                {/* 2️⃣ Телефон получателя */}
                <div className="flex items-center gap-4">
                  <span className={`w-3 h-3 rounded-full flex-shrink-0 ${getFieldStatus('receiverPhone') === 'auto' ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
                  <div className="flex-1">
                    <Label className="text-sm font-medium">2️⃣ Телефон получателя</Label>
                    <Input 
                      value={formData.receiverPhone}
                      onChange={(e) => setFormData(p => ({...p, receiverPhone: e.target.value}))}
                      placeholder="+7 999 123 45 67"
                      className={getFieldStatus('receiverPhone') === 'auto' ? 'bg-green-50 border-green-200' : ''}
                    />
                  </div>
                </div>

                {/* 3️⃣ Банк получателя */}
                <div className="flex items-center gap-4">
                  <span className="w-3 h-3 rounded-full flex-shrink-0 bg-yellow-500"></span>
                  <div className="flex-1">
                    <Label className="text-sm font-medium">3️⃣ Банк получателя</Label>
                    <Select onValueChange={(v) => setFormData(p => ({...p, receiverBank: v}))} value={formData.receiverBank}>
                      <SelectTrigger className={!formData.receiverBank ? 'border-yellow-300' : ''}>
                        <SelectValue placeholder="Выберите банк получателя..." />
                      </SelectTrigger>
                      <SelectContent>
                        {BANKS.map(bank => (
                          <SelectItem key={bank.id} value={bank.name}>{bank.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* 4️⃣ Имя получателя */}
                <div className="flex items-center gap-4">
                  <span className={`w-3 h-3 rounded-full flex-shrink-0 ${getFieldStatus('receiverName') === 'auto' ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
                  <div className="flex-1">
                    <Label className="text-sm font-medium">4️⃣ Имя получателя</Label>
                    <Input 
                      value={formData.receiverName}
                      onChange={(e) => setFormData(p => ({...p, receiverName: e.target.value}))}
                      placeholder="Иванов Иван Иванович"
                      className={getFieldStatus('receiverName') === 'auto' ? 'bg-green-50 border-green-200' : ''}
                    />
                  </div>
                </div>

                {/* 5️⃣ Имя отправителя */}
                <div className="flex items-center gap-4">
                  <span className={`w-3 h-3 rounded-full flex-shrink-0 ${getFieldStatus('senderName') === 'auto' ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
                  <div className="flex-1">
                    <Label className="text-sm font-medium">5️⃣ Имя отправителя</Label>
                    <Input 
                      value={formData.senderName}
                      onChange={(e) => setFormData(p => ({...p, senderName: e.target.value}))}
                      placeholder="Петров Пётр Петрович"
                      className={getFieldStatus('senderName') === 'auto' ? 'bg-green-50 border-green-200' : ''}
                    />
                  </div>
                </div>

                {/* 6️⃣ Дата и время */}
                <div className="flex items-center gap-4">
                  <span className={`w-3 h-3 rounded-full flex-shrink-0 ${getFieldStatus('dateTime') === 'auto' ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
                  <div className="flex-1">
                    <Label className="text-sm font-medium">6️⃣ Дата и время перевода</Label>
                    <Input 
                      value={formData.dateTime}
                      onChange={(e) => setFormData(p => ({...p, dateTime: e.target.value}))}
                      placeholder="29.01.2026 09:18"
                      className={getFieldStatus('dateTime') === 'auto' ? 'bg-green-50 border-green-200' : ''}
                    />
                  </div>
                </div>

                {/* ▶️ Номер СБП (необязательно) */}
                <div className="flex items-center gap-4">
                  <span className="w-3 h-3 rounded-full flex-shrink-0 bg-slate-300"></span>
                  <div className="flex-1">
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <Hash className="w-4 h-4" />
                      Номер операции СБП (необязательно)
                    </Label>
                    <Input 
                      value={formData.sbpNumber}
                      onChange={(e) => setFormData(p => ({...p, sbpNumber: e.target.value}))}
                      placeholder="Оставьте пустым для автогенерации"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <Button 
                  onClick={() => handleGenerate(true)}
                  disabled={!formData.amount || !formData.receiverPhone || !formData.receiverBank || !formData.receiverName || !formData.senderName || !formData.dateTime}
                  className="bg-green-500 hover:bg-green-600"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Локальный PDF (быстро)
                </Button>
                {authStatus?.authenticated && (
                  <Button 
                    onClick={() => handleGenerate(false)}
                    disabled={!formData.amount || !formData.receiverPhone || !formData.receiverBank || !formData.receiverName || !formData.senderName || !formData.dateTime}
                    variant="outline"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Через Telegram бота
                  </Button>
                )}
              </div>
            </Card>
          )}

          {/* Step 3: Generation Progress */}
          {formStep === 'generate' && (
            <Card className="p-6">
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                Генерация чека через @happychilds_bot
              </h3>
              
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {botConversation.map((msg, i) => (
                  <div key={i} className={`p-3 rounded-lg ${
                    msg.type === 'sent' ? 'bg-blue-100 ml-12' : 
                    msg.type === 'info' ? 'bg-slate-100' :
                    'bg-green-100 mr-12'
                  }`}>
                    <p className="text-slate-700 whitespace-pre-wrap">{msg.text}</p>
                  </div>
                ))}
                {processing && (
                  <div className="flex items-center justify-center p-4">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                    <span className="ml-2 text-slate-500">Отправка данных боту... (~10-30 сек)</span>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Step 4: Result */}
          {formStep === 'result' && (
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  Результат
                </h3>
                <Button variant="outline" onClick={resetForm}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Новый чек
                </Button>
              </div>

              {result?.pdf ? (
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 rounded-lg text-green-700">
                    ✅ PDF чек успешно создан!
                  </div>
                  <div className="flex gap-3">
                    <Button
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = `data:application/pdf;base64,${result.pdf}`;
                        link.download = result.filename || `check_${Date.now()}.pdf`;
                        link.click();
                      }}
                      className="bg-green-500 hover:bg-green-600"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Скачать PDF
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        const pdfWindow = window.open();
                        pdfWindow.document.write(
                          `<iframe width='100%' height='100%' src='data:application/pdf;base64,${result.pdf}'></iframe>`
                        );
                      }}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Открыть PDF
                    </Button>
                  </div>
                </div>
              ) : result?.image ? (
                <div className="space-y-4">
                  <div className="rounded-lg overflow-hidden border shadow-lg">
                    <img 
                      src={`data:image/jpeg;base64,${result.image}`}
                      alt="Generated check"
                      className="w-full max-w-2xl mx-auto"
                    />
                  </div>
                  <Button
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = `data:image/jpeg;base64,${result.image}`;
                      link.download = `check_${selectedBank?.id}_${Date.now()}.jpg`;
                      link.click();
                    }}
                    className="bg-green-500 hover:bg-green-600"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Скачать
                  </Button>
                </div>
              ) : (
                <div className="p-4 bg-yellow-50 rounded-lg">
                  <p className="text-yellow-700">
                    Результат не получен. Проверьте бота @happychilds_bot
                  </p>
                </div>
              )}
            </Card>
          )}
        </>
      )}
    </div>
  );
}
